/**
 * Upcoming Work board: date-of-work projection over Service Plan + Service Line + Task.
 *
 * A row click opens the OCCURRENCE (`taskId:visitDate`) in a read-only Detail, not the Service Line
 * behind it: clients clicking one visit landed in a Service Line workspace covering every visit, which
 * read as the wrong record. Both domains share the surface; only the rights gate differs.
 */
import { ActivityEntityName, DomainType, EntityViewDisplay, PortalClientSecurityRight, SurfaceType, UpcomingWork } from '@jms/model';
import { inject } from '@angular/core';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { RightsService } from '@jms/utils/util.permissions';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { FILTER_PERIOD_PRESETS, FilterPeriodPreset } from '@workbuddy/ui/v2/blocks/filter';
import { PlannedWorkCol } from '../shared/planned.work.board.columns';
import { visitListDefaultPreset } from '../visit/board/visit.period.default';
import { upcomingWorkApprovalTag } from './presentation/upcoming.work.approval.tag';
import { navigateToUpcomingWorkOccurrence } from './services/upcoming.work.board.open';
import { clientUpcomingWorkCsvCommands } from './services/upcoming.work.csv.download';
import type { EntityBoardColumnOverrides } from '@workbuddy/ui/v2/blocks/entity.board/context';
import type { EntityManifestSurfaces } from '@workbuddy/services/entities';
import { UpcomingWorkVisitDateCellRenderer } from './field.renderers/visit.date/upcoming.work.visit.date.cell.renderer';

// Shared by BOTH domain blocks. `domains` replaces the base manifest wholesale, so the portal repeats
// the declaration; only the value is reused, never inherited.
const upcomingWorkSurfaces: EntityManifestSurfaces<UpcomingWork> = {
  [SurfaceType.Detail]: () => import('./surface/detail/upcoming.work.occurrence.detail.surface').then(module => module.upcomingWorkOccurrenceDetailSurface),
};

// Shared by BOTH domain blocks, for the same reason as `upcomingWorkSurfaces`: an approval pill that
// rendered one way for the operator and another for their customer would make the two boards disagree
// about the same fact.
const upcomingWorkPresentation = { tags: { approvalStatus: upcomingWorkApprovalTag } };

const upcomingWorkColumns: EntityBoardColumnOverrides<UpcomingWork> = {
  byField: {
    visitDate: PlannedWorkCol.date,
    name: PlannedWorkCol.title,
    servicePlan: PlannedWorkCol.plan,
    customer: PlannedWorkCol.customer,
    siteAddress: PlannedWorkCol.site,
    frequency: PlannedWorkCol.frequency,
    approvalStatus: PlannedWorkCol.tag,
    approvedBy: PlannedWorkCol.customer,
    approvedAt: PlannedWorkCol.date,
    serviceType: PlannedWorkCol.tag,
    serviceScope: PlannedWorkCol.tag,
    workCategory: PlannedWorkCol.tag,
    zone: PlannedWorkCol.tag,
    siteCategory: PlannedWorkCol.tag,
  },
};

export const upcomingWorkManifest = registerManifest<UpcomingWork>({
  entity: ActivityEntityName.UpcomingWork,
  slug: 'upcoming-work',
  surfaces: upcomingWorkSurfaces,
  presentation: upcomingWorkPresentation,
  operations: {
    preview: { surface: SurfaceType.Detail },
  },
  board: {
    supportedDisplays: [EntityViewDisplay.Table],
    defaults: {
      /**
       * Upcoming Work is read one PERIOD at a time, never unbounded.
       *
       * The forecast walks a twelve-month projection, so a board asking for "everything" pays for all
       * of it; the API reader already refuses, defaulting to a 30-day window and clamping anything
       * wider than its horizon. Declaring the period makes that bound VISIBLE and steerable instead of
       * a silent server-side default, and it replaces the `visitDate` quick-filter chip the view used
       * to carry — the period always wins over a view filter on the same field, so a chip could only
       * ever contradict the header without changing the read.
       */
      period: {
        field: 'visitDate',
        // All nine: forecast work is asked about backwards (what did we service last week) as readily
        // as forwards, and the reader answers a bounded past range as happily as a future one.
        presets: [...FILTER_PERIOD_PRESETS],
        // Tenant policy, the same setting the Visits board opens on — one answer to "what does now
        // mean here", so the two planned-work boards agree about the current stretch of time.
        defaultPreset: () => visitListDefaultPreset(inject(GeneralSettingService).generalSettings),
        custom: true,
      },
      // Upcoming Work is a forecast projection: counting the window exactly means walking all of it,
      // which was the single largest cost of the read (WBS-11112). The reader reports `hasMore`, so the
      // board pages with a stepper rather than a numbered paginator.
      paging: 'hasMore',
      empty: { title: 'No matching upcoming work.' },
      table: {
        fillerColumn: true,
        columns: upcomingWorkColumns,
        // The date cell carries the moved and blacked-out-day badges, as the portal's does.
        overrideCellRenderer: (_context, _metadata, field, renderer) => field.name === 'visitDate'
          ? { ...renderer, cellRenderer: UpcomingWorkVisitDateCellRenderer }
          : renderer,
      },
      // Service Line is filterable for the occurrence surface's sibling read; the board pickers keep
      // it hidden until product decides it belongs there.
      shapeFilterFields: fields => fields.filter(field => field.name !== 'serviceLine'),
      header: {
        title: 'Upcoming Work',
        showSearch: true,
        showPaginator: true,
        showViewOptions: true,
        hideAdd: true,
      },
      itemInteraction: {
        // To revert to the pre-2026-09 behaviour (row opens the Service Line workspace), point this
        // at `navigateToUpcomingWork`; it is retained for exactly that. Same swap in the portal block.
        navigate: navigateToUpcomingWorkOccurrence,
      },
    },
  },
  domains: {
    [DomainType.ClientPortal]: {
      // No Upcoming Work page in the portal: the occurrence workspace IS the inspector. Declaring a
      // record resolver that answers `null` hides Open full page / Open in new tab, which a domain
      // declaring none would still inherit from the generic derived route.
      routes: {
        root: '/services',
        record: () => null,
      },
      surfaces: upcomingWorkSurfaces,
      // Repeated, not inherited: `resolveEntityManifest` builds a non-Tenant manifest from the domain
      // entry alone, so a `presentation` declared only on the base would leave the portal's approval
      // column rendering a bare string where the tenant board shows a pill.
      presentation: upcomingWorkPresentation,
      operations: {
        preview: { surface: SurfaceType.Detail, authorize: ({ injector }) => injector.get(RightsService).hasRights(PortalClientSecurityRight.CanAccessServices) || injector.get(RightsService).hasRights(PortalClientSecurityRight.SiteServicesView) },
      },
      board: {
        supportedDisplays: [EntityViewDisplay.Table],
        defaults: {
          /**
           * Same bounded read as the tenant board, repeated because `resolveEntityManifest` builds a
           * non-Tenant manifest from this block ALONE — a period declared only on the base would leave
           * the portal unbounded.
           *
           * It replaces the fixed `NextXDays: 30` pre-filter this board used to carry: that bound was
           * correct but unmovable, so a client asking "what did you do last month" had no way to ask.
           */
          period: {
            field: 'visitDate',
            presets: [...FILTER_PERIOD_PRESETS],
            // Not the tenant setting: a portal session reads no tenant settings, and a client's sense
            // of "now" is their billing month rather than an operator's planning fortnight.
            defaultPreset: () => FilterPeriodPreset.ThisMonth,
            custom: true,
          },
          // Same forecast reader as the tenant board. A domain manifest REPLACES the base rather than
          // inheriting it, so the flag has to be repeated here.
          paging: 'hasMore',
          readonly: true,
          empty: { title: 'No upcoming work found.' },
          table: {
            fillerColumn: true,
            columns: { byField: { ...upcomingWorkColumns.byField, visitDate: { width: 130, minWidth: 130, flex: 0 } } },
            overrideCellRenderer: (_context, _metadata, field, renderer) => field.name === 'visitDate'
              ? { ...renderer, cellRenderer: UpcomingWorkVisitDateCellRenderer }
              : renderer,
          },
          shapeFilterFields: fields => fields.filter(field => !['customer', 'parent', 'servicePlan', 'serviceLine', 'workCategory'].includes(field.name)),
          header: {
            title: 'Services',
            showSearch: true,
            showPaginator: true,
            showViewOptions: false,
            hideAdd: true,
            primaryCommands: clientUpcomingWorkCsvCommands,
            // This board is `readonly`, which drops header commands as mutation entry points. Download
            // CSV writes nothing — it exports the rows already on screen — so it opts back in rather
            // than the board giving up readonly and restoring Add, row commands, selection and bulk.
            keepPrimaryCommandsWhenReadonly: true,
          },
          itemInteraction: {
            // Revert: `navigateToUpcomingWork` (see the tenant block).
            navigate: navigateToUpcomingWorkOccurrence,
          },
        },
      },
    },
  },
});
